import React from "react";
import { FmButton } from "./FmButton";
import type {
  FmMapPanelDescriptor,
  FmMapWorkspaceDescriptor,
} from "./FmMapWorkspaceRoot.types";
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

/**
 * Typed panels are named by their kind. A section panel's kind says only that
 * it carries shared section content, so its id names it instead (the
 * workspace's `evidence` panel keeps `map-panel-evidence-toggle`).
 */
function panelToggleKey(panel: FmMapPanelDescriptor): string {
  return panel.kind === "section" ? panel.id : panel.kind;
}

export function FmMapWorkspacePreviewPanelTools({
  activePanelId,
  descriptor,
  isPreview,
  setActivePanelId,
}: {
  activePanelId: string | null;
  descriptor: FmMapWorkspaceDescriptor;
  isPreview: boolean;
  setActivePanelId: (panelId: string | null) => void;
}): React.ReactElement | null {
  if (!isPreview) return null;
  return (
    <nav
      className="fm-map-workspace__tools"
      aria-label="Map workspace panels"
      {...fmMapOcclusion('left', 'panel')}
    >
      {descriptor.actions.filter(({ tool }) => Boolean(tool)).map((action) => (
        <FmButton
          key={action.id}
          variant={action.intent === "primary" ? "primary" : "bordered"}
          disabled={Boolean(action.disabledReason)}
          title={action.disabledReason}
          onClick={() => descriptor.onEvent?.({
            kind: "action",
            actionId: action.id,
          })}
          data-testid={`map-preview-action-${action.id}`}
        >
          {action.label}
        </FmButton>
      ))}
      {descriptor.panels.map((panel) => (
        <FmButton
          key={panel.id}
          variant={activePanelId === panel.id ? "primary" : "bordered"}
          aria-pressed={activePanelId === panel.id}
          onClick={() => {
            const next = activePanelId === panel.id ? null : panel.id;
            setActivePanelId(next);
            descriptor.onEvent?.({ kind: "panel-change", panelId: next });
          }}
          data-testid={`map-panel-${panelToggleKey(panel)}-toggle`}
        >
          {panel.title}
        </FmButton>
      ))}
    </nav>
  );
}
